import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PhoneCall } from 'lucide-react';
import { z } from 'zod';
import {
  FormDialog,
  FormSection,
  FormSelectField,
  FormTextareaField,
} from '@/components/common/FormDialog';
import {
  CALL_RESULT_LABEL,
  CALL_RESULT_OPTIONS,
  DONATION_COMMENT_MAX_LENGTH,
  TEACHER_ANSWER_LABEL,
  TEACHER_ANSWER_OPTIONS,
} from '../constants/marketPromotion.constants';
import { useVerifyMis } from '../hooks/useMpMutations';
import type { MpApprovalRow } from '../types/marketPromotion.types';

const misVerifyFormSchema = z.object({
  callStatus: z.number().optional(),
  teacherFeedback: z.number().optional(),
  misComment: z.string().max(DONATION_COMMENT_MAX_LENGTH),
});

type MisVerifyFormValues = z.infer<typeof misVerifyFormSchema>;

function toKnownCode(
  labels: Partial<Record<number, string>>,
  code: number | null,
): number | undefined {
  return code !== null && labels[code] !== undefined ? code : undefined;
}

function getSavedVerifyValues(donation: MpApprovalRow): MisVerifyFormValues {
  return {
    callStatus: toKnownCode(CALL_RESULT_LABEL, donation.callStatus),
    teacherFeedback: toKnownCode(TEACHER_ANSWER_LABEL, donation.teacherFeedback),
    misComment: donation.misComment ?? '',
  };
}

interface MisVerifyDialogProps {
  donation: MpApprovalRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MisVerifyDialog({ donation, open, onOpenChange }: MisVerifyDialogProps) {
  const form = useForm<MisVerifyFormValues>({
    resolver: zodResolver(misVerifyFormSchema),
    defaultValues: getSavedVerifyValues(donation),
  });
  const verifyMutation = useVerifyMis(() => onOpenChange(false));

  function handleVerify(values: MisVerifyFormValues) {
    const misComment = values.misComment.trim();
    verifyMutation.mutate({
      detailId: donation.detailId,
      payload: {
        ...(values.callStatus === undefined ? {} : { callStatus: values.callStatus }),
        ...(values.teacherFeedback === undefined
          ? {}
          : { teacherFeedback: values.teacherFeedback }),
        ...(misComment === '' ? {} : { misComment }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={PhoneCall}
      title="MIS Verify"
      description={donation.instituteName ?? donation.itemName}
      form={form}
      isPending={verifyMutation.isPending}
      isEditMode={donation.isMisVerified}
      submitLabel={donation.isMisVerified ? 'Update' : 'Verify'}
      onSubmit={handleVerify}
    >
      <FormSection>
        <FormSelectField
          control={form.control}
          name="callStatus"
          label="Call Result"
          options={CALL_RESULT_OPTIONS}
          isLoading={false}
          placeholder="Pick the call result"
        />
        <FormSelectField
          control={form.control}
          name="teacherFeedback"
          label="Teacher Answer"
          options={TEACHER_ANSWER_OPTIONS}
          isLoading={false}
          placeholder="Pick the teacher's answer"
        />
        <FormTextareaField
          control={form.control}
          name="misComment"
          label="MIS Comment"
          placeholder="What the teacher said (optional)"
          maxLength={DONATION_COMMENT_MAX_LENGTH}
          isFullRow
        />
      </FormSection>
    </FormDialog>
  );
}
